// Slice 2N · Job Profitability Intelligence — Contractor Workspace
// (PASS 2).
//
// The full contractor-facing surface. Consumes the PASS 1 backend
// endpoints verbatim and never invents totals. Six connected
// sections: Overview, Revenue, Costs, Cost Entries & Supporting
// Documents, Levi's Review, and Improve the Next Job.
//
// Governing rule: "Levi recommends. The contractor decides."
//   * No revenue, margin, cost, or confirmation value is mutated
//     implicitly. Every write is a deliberate contractor action.
//   * Backend deterministic values are the single source of truth
//     for all displayed numbers.
//   * Unsupported values are shown as "not available yet" — never as
//     $0 pretending to be authoritative.
//   * Unconfirmed and excluded entries are visually distinct from
//     confirmed actual costs.
//   * Confirmed-entry correction requires the deliberate correction
//     flow surfaced by the backend.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import {
  ArrowLeft, LineChart, Loader2, Plus, CheckCircle2, XCircle,
  RotateCcw, AlertTriangle, ShieldCheck, Sparkles, FileText,
  Pencil, DollarSign, Info,
} from "lucide-react";

const COST_CATEGORIES = [
  "materials", "labor", "subcontractors", "equipment_rentals",
  "disposal", "permits_fees", "delivery_travel", "other_direct",
];
const COST_CLASSIFICATIONS = ["actual", "committed"];

const moneyFmt = (v, lang) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat(lang === "es" ? "es-US" : "en-US", {
      style: "currency", currency: "USD",
      maximumFractionDigits: 2,
    }).format(n);
  } catch (_) { return `$${n.toFixed(2)}`; }
};

const pctFmt = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return `${Number(v).toFixed(2)} %`;
};

// ---------------------------------------------------------------------
// Small presentational bits.
// ---------------------------------------------------------------------

const StatValue = ({ label, value, supported = true, testid, lang }) => (
  <div data-testid={testid} className="min-w-0">
    <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
      {label}
    </div>
    <div className={`text-base font-semibold break-words ${supported ? "" : "text-[var(--jc-text-soft)] italic"}`}>
      {/*
        D-1 / D-5 correction: when a value is unsupported, render the
        caller-supplied honest text verbatim. Never coerce it back to
        $0.00 via moneyFmt(0) — that misrepresents an unavailable
        value as an authoritative zero. Supported empty totals may
        still legitimately arrive here as "$0.00" (via moneyFmt(0)
        from the caller); those pass through unchanged.
      */}
      {value}
    </div>
  </div>
);

// D-2 correction: unsupported cells always display the neutral
// "Not available yet" (or Spanish equivalent) as the primary value.
// Any actionable guidance appears as a small line *below* the primary
// value — it never replaces it. The caller supplies the neutral
// primary via `primaryText` and any actionable hint via `hint`.
const UnavailableValue = ({ label, primaryText, hint, testid }) => (
  <div data-testid={testid} className="min-w-0">
    <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
      {label}
    </div>
    <div className="text-base font-semibold text-[var(--jc-text-soft)] italic break-words">
      {primaryText}
    </div>
    {hint ? (
      <div className="text-xs text-[var(--jc-text-soft)] break-words mt-0.5">
        {hint}
      </div>
    ) : null}
  </div>
);

const StatePill = ({ state, t }) => {
  const tone = {
    not_started: "bg-slate-100 border-slate-200 text-slate-700",
    projected_only: "bg-sky-50 border-sky-200 text-sky-800",
    partially_tracked: "bg-amber-50 border-amber-200 text-amber-800",
    actuals_in_progress: "bg-indigo-50 border-indigo-200 text-indigo-800",
    actuals_confirmed: "bg-emerald-50 border-emerald-300 text-emerald-800",
  }[state] || "bg-slate-100 border-slate-200 text-slate-700";
  return (
    <span
      data-testid="profitability-state-pill"
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${tone}`}
    >
      {t(`profitability.state.${state}`)}
    </span>
  );
};

// ---------------------------------------------------------------------
// Overview section.
// ---------------------------------------------------------------------

const OverviewSection = ({ snap, t, lang }) => {
  const rev = snap.revenue || {};
  const exp = snap.expected || {};
  const act = snap.actuals || {};
  const prof = snap.profit || {};
  return (
    <section data-testid="profitability-section-overview" className="jc-card p-5 mb-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="overline">{t("profitability.section.overview_overline")}</div>
          <h2 className="text-lg font-semibold">{t("profitability.section.overview_title")}</h2>
        </div>
        <StatePill state={snap.state || "not_started"} t={t} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {rev.original_supported ? (
          <StatValue
            testid="ov-original-contract"
            label={t("profitability.ov.original_contract")}
            value={moneyFmt(rev.original_contract_value, lang)}
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="ov-original-contract"
            label={t("profitability.ov.original_contract")}
            primaryText={t("profitability.value_unavailable")}
            hint={t("profitability.hint.needs_estimate")}
          />
        )}
        {rev.approved_changes_supported ? (
          <StatValue
            testid="ov-approved-changes"
            label={t("profitability.ov.approved_changes")}
            value={moneyFmt(rev.approved_changes_amount, lang)}
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="ov-approved-changes"
            label={t("profitability.ov.approved_changes")}
            primaryText={t("profitability.value_unavailable")}
            hint={t("profitability.ov.approved_changes_unsupported")}
          />
        )}
        {rev.original_supported ? (
          <StatValue
            testid="ov-revised-contract"
            label={t("profitability.ov.revised_contract")}
            value={moneyFmt(rev.revised_contract_value, lang)}
            supported
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="ov-revised-contract"
            label={t("profitability.ov.revised_contract")}
            primaryText={t("profitability.value_unavailable")}
            hint={t("profitability.hint.needs_estimate")}
          />
        )}
        {/*
          D-3 correction: display the contractor-entered expected
          margin whenever settings.expected_margin_pct exists — do
          NOT gate on exp.supported (which requires supported revenue).
          The entered percentage is a contractor assumption; the
          revenue-derived expected profit + implied cost remain gated
          on supported revenue.
        */}
        {exp.expected_margin_pct != null ? (
          <StatValue
            testid="ov-expected-margin"
            label={t("profitability.ov.expected_margin")}
            value={pctFmt(exp.expected_margin_pct)}
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="ov-expected-margin"
            label={t("profitability.ov.expected_margin")}
            primaryText={t("profitability.value_unavailable")}
            hint={t("profitability.hint.enter_expected_margin_below")}
          />
        )}
        {exp.supported ? (
          <StatValue
            testid="ov-expected-profit"
            label={t("profitability.ov.expected_profit")}
            value={moneyFmt(exp.expected_profit_amount, lang)}
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="ov-expected-profit"
            label={t("profitability.ov.expected_profit")}
            primaryText={t("profitability.value_unavailable")}
            hint={
              exp.expected_margin_pct == null
                ? t("profitability.hint.enter_expected_margin_below")
                : t("profitability.hint.needs_estimate")
            }
          />
        )}
        {exp.supported ? (
          <StatValue
            testid="ov-implied-cost"
            label={t("profitability.ov.implied_cost")}
            value={moneyFmt(exp.implied_expected_cost, lang)}
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="ov-implied-cost"
            label={t("profitability.ov.implied_cost")}
            primaryText={t("profitability.value_unavailable")}
            hint={
              exp.expected_margin_pct == null
                ? t("profitability.hint.enter_expected_margin_below")
                : t("profitability.hint.needs_estimate")
            }
          />
        )}
        <StatValue
          testid="ov-committed-total"
          label={t("profitability.ov.committed_total")}
          value={moneyFmt(act.committed_total, lang)}
          lang={lang}
        />
        <StatValue
          testid="ov-actual-confirmed"
          label={t("profitability.ov.actual_confirmed")}
          value={moneyFmt(act.actual_confirmed_total, lang)}
          lang={lang}
        />
        {prof.supported ? (
          <StatValue
            testid="ov-actual-profit"
            label={t("profitability.ov.actual_profit")}
            value={moneyFmt(prof.actual_profit_amount, lang)}
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="ov-actual-profit"
            label={t("profitability.ov.actual_profit")}
            primaryText={t("profitability.value_unavailable")}
            hint={t(`profitability.hint.profit_reason.${prof.reason || "no_supported_revenue"}`)}
          />
        )}
        {prof.supported ? (
          <StatValue
            testid="ov-actual-margin"
            label={t("profitability.ov.actual_margin")}
            value={pctFmt(prof.actual_margin_pct)}
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="ov-actual-margin"
            label={t("profitability.ov.actual_margin")}
            primaryText={t("profitability.value_unavailable")}
            hint={t(`profitability.hint.profit_reason.${prof.reason || "no_supported_revenue"}`)}
          />
        )}
        {exp.supported ? (
          <StatValue
            testid="ov-remaining-vs-confirmed"
            label={t("profitability.ov.remaining_vs_confirmed")}
            value={moneyFmt(act.remaining_vs_confirmed, lang)}
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="ov-remaining-vs-confirmed"
            label={t("profitability.ov.remaining_vs_confirmed")}
            primaryText={t("profitability.value_unavailable")}
            hint={
              exp.expected_margin_pct == null
                ? t("profitability.hint.enter_expected_margin_below")
                : t("profitability.hint.needs_estimate")
            }
          />
        )}
      </div>

      {Array.isArray(snap.missing_data) && snap.missing_data.length > 0 && (
        <div
          data-testid="profitability-missing-data"
          className="mt-4 border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-3 text-xs"
        >
          <div className="font-semibold mb-1 flex items-center gap-1.5">
            <AlertTriangle size={14} />
            {t("profitability.missing_data_heading")}
          </div>
          <ul className="list-disc ml-5 space-y-0.5">
            {snap.missing_data.map((flag) => (
              <li key={flag} data-testid={`missing-flag-${flag}`}>
                {t(`profitability.missing_flag.${flag}`)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------
// Revenue section.
// ---------------------------------------------------------------------

const RevenueSection = ({ snap, t, lang }) => {
  const rev = snap.revenue || {};
  const source = rev.original_source || "none";
  return (
    <section data-testid="profitability-section-revenue" className="jc-card p-5 mb-6">
      <div className="overline">{t("profitability.section.revenue_overline")}</div>
      <h2 className="text-lg font-semibold mb-1">{t("profitability.section.revenue_title")}</h2>
      {/* D-7 correction: contractor-facing label for the revenue source. */}
      <div
        data-testid="rev-source-label"
        className="text-xs text-[var(--jc-text-soft)] mb-4"
      >
        <span className="font-semibold text-[var(--jc-text)]">
          {t("profitability.rev.source_label")}:
        </span>{" "}
        <span data-testid={`rev-source-${source}`}>
          {t(`profitability.rev.source.${source}`)}
        </span>
        {source === "estimate_direct" && (
          <span className="ml-1 text-[var(--jc-text-soft)]">
            — {t("profitability.rev.source_fallback_note")}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {rev.original_supported ? (
          <StatValue
            testid="rev-original"
            label={t("profitability.rev.original_contract")}
            value={moneyFmt(rev.original_contract_value, lang)}
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="rev-original"
            label={t("profitability.rev.original_contract")}
            primaryText={t("profitability.value_unavailable")}
            hint={t("profitability.hint.needs_estimate")}
          />
        )}
        {rev.approved_changes_supported ? (
          <StatValue
            testid="rev-approved-changes"
            label={t("profitability.rev.approved_changes")}
            value={moneyFmt(rev.approved_changes_amount, lang)}
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="rev-approved-changes"
            label={t("profitability.rev.approved_changes")}
            primaryText={t("profitability.value_unavailable")}
            hint={t("profitability.rev.approved_changes_unsupported_title")}
          />
        )}
        {rev.original_supported ? (
          <StatValue
            testid="rev-revised"
            label={t("profitability.rev.revised_contract")}
            value={moneyFmt(rev.revised_contract_value, lang)}
            lang={lang}
          />
        ) : (
          <UnavailableValue
            testid="rev-revised"
            label={t("profitability.rev.revised_contract")}
            primaryText={t("profitability.value_unavailable")}
            hint={t("profitability.hint.needs_estimate")}
          />
        )}
        <StatValue
          testid="rev-invoiced"
          label={t("profitability.rev.invoiced")}
          value={moneyFmt(rev.invoiced_amount, lang)}
          lang={lang}
        />
        <StatValue
          testid="rev-collected"
          label={t("profitability.rev.collected")}
          value={moneyFmt(rev.collected_amount, lang)}
          lang={lang}
        />
        <StatValue
          testid="rev-remaining"
          label={t("profitability.rev.remaining_receivable")}
          value={moneyFmt(rev.remaining_receivable, lang)}
          lang={lang}
        />
      </div>
      {!rev.approved_changes_supported && (
        <div
          data-testid="rev-approved-changes-note"
          className="mt-4 border border-slate-200 bg-slate-50 rounded-md p-3 text-xs text-[var(--jc-text-soft)] flex items-start gap-2"
        >
          <Info size={14} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-[var(--jc-text)]">
              {t("profitability.rev.approved_changes_unsupported_title")}
            </div>
            <div>{t("profitability.rev.approved_changes_unsupported_body")}</div>
          </div>
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------
// Costs section (aggregate summary).
// ---------------------------------------------------------------------

const CostsSection = ({ snap, t, lang }) => {
  const act = snap.actuals || {};
  const categoryTotals = act.category_totals || {};
  return (
    <section data-testid="profitability-section-costs" className="jc-card p-5 mb-6">
      <div className="overline">{t("profitability.section.costs_overline")}</div>
      <h2 className="text-lg font-semibold mb-4">{t("profitability.section.costs_title")}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatValue
          testid="costs-committed-total"
          label={t("profitability.costs.committed_total")}
          value={moneyFmt(act.committed_total, lang)}
          lang={lang}
        />
        <StatValue
          testid="costs-actual-confirmed"
          label={t("profitability.costs.actual_confirmed")}
          value={moneyFmt(act.actual_confirmed_total, lang)}
          lang={lang}
        />
        <StatValue
          testid="costs-unconfirmed-count"
          label={t("profitability.costs.unconfirmed_count")}
          value={String(act.unconfirmed_count || 0)}
          lang={lang}
        />
        <StatValue
          testid="costs-excluded-count"
          label={t("profitability.costs.excluded_count")}
          value={String(act.excluded_count || 0)}
          lang={lang}
        />
      </div>
      <div className="border border-[var(--jc-border)] rounded-md overflow-hidden">
        <div className="hidden md:grid md:grid-cols-2 bg-[var(--jc-bg)] px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          <div>{t("profitability.costs.category_col")}</div>
          <div className="text-right">{t("profitability.costs.total_col")}</div>
        </div>
        {COST_CATEGORIES.map((cat) => (
          <div
            key={cat}
            data-testid={`costs-cat-${cat}`}
            className="grid grid-cols-2 px-3 py-2 border-t border-[var(--jc-border)] first:border-t-0 md:first:border-t md:border-t text-sm"
          >
            <div>{t(`profitability.category.${cat}`)}</div>
            <div className="text-right font-medium">
              {moneyFmt(categoryTotals[cat] || 0, lang)}
            </div>
          </div>
        ))}
      </div>
      {(act.unconfirmed_count || 0) > 0 && (
        <div
          data-testid="costs-unconfirmed-hint"
          className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-start gap-2"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>{t("profitability.costs.unconfirmed_hint")}</div>
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------
// Cost entries + document suggestions.
// ---------------------------------------------------------------------

const emptyEntryDraft = {
  amount: "", category: "materials", classification: "actual",
  vendor: "", incurred_date: "", description: "",
};

const emptyDocDraft = {
  source_document_id: "", amount: "", category: "materials",
  classification: "actual", vendor: "", incurred_date: "", description: "",
};

const EntryRow = ({ entry, onConfirm, onExclude, onRestore, onEdit, t, lang }) => {
  const excluded = !!entry.excluded;
  const confirmed = !!entry.confirmed;
  const isSuggestion = entry.source === "document_suggestion";
  const tone = excluded
    ? "bg-slate-50 opacity-60"
    : isSuggestion && !confirmed
      ? "bg-indigo-50/50 border-indigo-100"
      : confirmed
        ? ""
        : "bg-amber-50/50";
  return (
    <div
      data-testid={`cost-entry-row-${entry.id}`}
      className={`border border-[var(--jc-border)] rounded-md p-3 grid grid-cols-1 md:grid-cols-6 gap-2 items-start ${tone}`}
    >
      <div className="md:col-span-2 min-w-0">
        <div className="text-sm font-semibold break-words">
          {entry.description || t(`profitability.category.${entry.category}`)}
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          <span
            data-testid={`entry-category-${entry.id}`}
            className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-50 border-slate-200 uppercase tracking-wider"
          >
            {t(`profitability.category.${entry.category}`)}
          </span>
          <span
            data-testid={`entry-classification-${entry.id}`}
            className={`text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-wider ${
              entry.classification === "committed"
                ? "bg-sky-50 border-sky-200 text-sky-800"
                : "bg-emerald-50 border-emerald-200 text-emerald-800"
            }`}
          >
            {t(`profitability.classification.${entry.classification}`)}
          </span>
          {isSuggestion && (
            <span
              data-testid={`entry-source-${entry.id}`}
              className="text-[10px] px-1.5 py-0.5 rounded border bg-indigo-50 border-indigo-200 text-indigo-800 uppercase tracking-wider"
            >
              {t("profitability.entry.source_document_suggestion")}
            </span>
          )}
          {/* D-9 · positive MANUAL source pill for non-suggestion entries. */}
          {!isSuggestion && !excluded && (
            <span
              data-testid={`entry-source-manual-${entry.id}`}
              className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-50 border-slate-300 text-slate-700 uppercase tracking-wider"
            >
              {t("profitability.entry.source_manual")}
            </span>
          )}
          {/* D-10 · positive INCLUDED pill; disappears when excluded. */}
          {!excluded && (
            <span
              data-testid={`entry-included-${entry.id}`}
              className="text-[10px] px-1.5 py-0.5 rounded border bg-teal-50 border-teal-300 text-teal-800 uppercase tracking-wider"
            >
              {t("profitability.entry.included_badge")}
            </span>
          )}
          {excluded && (
            <span
              data-testid={`entry-excluded-${entry.id}`}
              className="text-[10px] px-1.5 py-0.5 rounded border bg-slate-100 border-slate-300 text-slate-700 uppercase tracking-wider"
            >
              {t("profitability.entry.excluded_badge")}
            </span>
          )}
          {confirmed && !excluded && (
            <span
              data-testid={`entry-confirmed-${entry.id}`}
              className="text-[10px] px-1.5 py-0.5 rounded border bg-emerald-50 border-emerald-300 text-emerald-800 uppercase tracking-wider"
            >
              {t("profitability.entry.confirmed_badge")}
            </span>
          )}
          {!confirmed && !excluded && (
            <span
              data-testid={`entry-unconfirmed-${entry.id}`}
              className="text-[10px] px-1.5 py-0.5 rounded border bg-amber-50 border-amber-300 text-amber-800 uppercase tracking-wider"
            >
              {t("profitability.entry.unconfirmed_badge")}
            </span>
          )}
          {Array.isArray(entry.correction_history) && entry.correction_history.length > 0 && (
            <span
              data-testid={`entry-corrected-${entry.id}`}
              className="text-[10px] px-1.5 py-0.5 rounded border bg-purple-50 border-purple-200 text-purple-800 uppercase tracking-wider"
            >
              {t("profitability.entry.corrected_badge")}
            </span>
          )}
        </div>
        <div className="text-xs text-[var(--jc-text-soft)] mt-1 break-words">
          {entry.vendor ? `${entry.vendor}` : ""}
          {entry.vendor && entry.incurred_date ? " · " : ""}
          {entry.incurred_date || ""}
        </div>
        {entry.source_document_id && (
          <div
            data-testid={`entry-source-doc-${entry.id}`}
            className="text-xs text-[var(--jc-text-soft)] mt-1 flex items-center gap-1"
          >
            <FileText size={12} />
            {t("profitability.entry.linked_document")} · {entry.source_document_id}
          </div>
        )}
      </div>
      <div className="text-sm font-semibold md:text-right md:col-span-1">
        {moneyFmt(entry.amount, lang)}
      </div>
      <div className="md:col-span-3 flex flex-wrap gap-2 justify-start md:justify-end">
        {!excluded && !confirmed && (
          <button
            data-testid={`confirm-entry-${entry.id}`}
            onClick={() => onConfirm(entry)}
            className="text-xs font-semibold px-2.5 py-1 rounded-md jc-btn-primary inline-flex items-center gap-1"
          >
            <CheckCircle2 size={12} /> {t("profitability.entry.confirm")}
          </button>
        )}
        {!excluded && confirmed && (
          <button
            data-testid={`correct-entry-${entry.id}`}
            onClick={() => onEdit(entry, true)}
            className="text-xs font-semibold px-2.5 py-1 rounded-md border border-[var(--jc-border)] hover:bg-[var(--jc-bg)] inline-flex items-center gap-1"
          >
            <Pencil size={12} /> {t("profitability.entry.correct")}
          </button>
        )}
        {!excluded && !confirmed && (
          <button
            data-testid={`edit-entry-${entry.id}`}
            onClick={() => onEdit(entry, false)}
            className="text-xs font-semibold px-2.5 py-1 rounded-md border border-[var(--jc-border)] hover:bg-[var(--jc-bg)] inline-flex items-center gap-1"
          >
            <Pencil size={12} /> {t("profitability.entry.edit")}
          </button>
        )}
        {!excluded ? (
          <button
            data-testid={`exclude-entry-${entry.id}`}
            onClick={() => onExclude(entry)}
            className="text-xs font-semibold px-2.5 py-1 rounded-md border border-rose-200 text-rose-800 hover:bg-rose-50 inline-flex items-center gap-1"
          >
            <XCircle size={12} /> {t("profitability.entry.exclude")}
          </button>
        ) : (
          <button
            data-testid={`restore-entry-${entry.id}`}
            onClick={() => onRestore(entry)}
            className="text-xs font-semibold px-2.5 py-1 rounded-md border border-[var(--jc-border)] hover:bg-[var(--jc-bg)] inline-flex items-center gap-1"
          >
            <RotateCcw size={12} /> {t("profitability.entry.restore")}
          </button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Levi's Review — reads the deterministic Levi context and renders a
// small set of practical observations. No LLM call; no free-form
// reasoning. Every observation is a direct read of a backend field.
// ---------------------------------------------------------------------

const LeviReviewSection = ({ ctx, t, lang }) => {
  if (!ctx) return null;
  const obs = [];
  if (ctx.confirmed) {
    obs.push({ key: "confirmed", tone: "emerald",
      text: t("profitability.levi.confirmed_state") });
  } else if (ctx.state === "actuals_in_progress") {
    obs.push({ key: "in_progress", tone: "indigo",
      text: t("profitability.levi.in_progress") });
  } else if (ctx.state === "partially_tracked") {
    obs.push({ key: "partial", tone: "amber",
      text: t("profitability.levi.partial") });
  } else if (ctx.state === "projected_only") {
    obs.push({ key: "projected", tone: "sky",
      text: t("profitability.levi.projected_only") });
  } else {
    obs.push({ key: "not_started", tone: "slate",
      text: t("profitability.levi.not_started") });
  }
  if ((ctx.actuals?.unconfirmed_count || 0) > 0) {
    obs.push({ key: "unconfirmed", tone: "amber",
      text: t("profitability.levi.unconfirmed", ctx.actuals.unconfirmed_count) });
  }
  if (!ctx.expected?.expected_margin_pct && ctx.expected?.expected_margin_pct !== 0) {
    obs.push({ key: "no_margin", tone: "amber",
      text: t("profitability.levi.no_margin") });
  }
  if (!ctx.revenue?.original_supported) {
    obs.push({ key: "no_revenue", tone: "amber",
      text: t("profitability.levi.no_revenue") });
  }
  if (ctx.revenue && !ctx.revenue.approved_changes_supported) {
    obs.push({ key: "approved_changes_unsupported", tone: "slate",
      text: t("profitability.levi.approved_changes_unsupported") });
  }
  if (ctx.profit?.supported && ctx.expected?.expected_margin_pct != null
      && ctx.profit?.actual_margin_pct != null) {
    const diff = ctx.profit.actual_margin_pct - ctx.expected.expected_margin_pct;
    if (Math.abs(diff) >= 1) {
      obs.push({
        key: "margin_variance", tone: diff < 0 ? "rose" : "emerald",
        text: diff < 0
          ? t("profitability.levi.margin_below", Math.abs(diff).toFixed(2))
          : t("profitability.levi.margin_above", diff.toFixed(2)),
      });
    }
  }
  return (
    <section data-testid="profitability-section-levi-review" className="jc-card p-5 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} className="text-[var(--jc-navy)]" />
        <div className="overline">{t("profitability.section.levi_overline")}</div>
      </div>
      <h2 className="text-lg font-semibold mb-2">
        {t("profitability.section.levi_title")}
      </h2>
      <p className="text-xs text-[var(--jc-text-soft)] mb-3">
        {t("profitability.section.levi_subtitle")}
      </p>
      <ul className="space-y-2" data-testid="levi-review-observations">
        {obs.map((o) => (
          <li
            key={o.key}
            data-testid={`levi-obs-${o.key}`}
            className={`text-sm rounded-md border px-3 py-2 ${
              o.tone === "emerald"
                ? "bg-emerald-50 border-emerald-200"
                : o.tone === "amber"
                  ? "bg-amber-50 border-amber-200"
                  : o.tone === "rose"
                    ? "bg-rose-50 border-rose-200"
                    : o.tone === "sky"
                      ? "bg-sky-50 border-sky-200"
                      : o.tone === "indigo"
                        ? "bg-indigo-50 border-indigo-200"
                        : "bg-slate-50 border-slate-200"
            }`}
          >
            {o.text}
          </li>
        ))}
      </ul>
      <div className="mt-3 text-[11px] text-[var(--jc-text-soft)] italic">
        {t("profitability.levi.promise")}
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------
// Improve the Next Job — practical learning prompts derived from the
// deterministic snapshot. Never mutates pricing settings.
// ---------------------------------------------------------------------

const ImproveNextJobSection = ({ snap, t, lang }) => {
  const act = snap.actuals || {};
  const exp = snap.expected || {};
  const cats = act.category_totals || {};
  const total = Number(act.actual_confirmed_total || 0);
  const items = [];
  if (total > 0) {
    const rank = COST_CATEGORIES
      .map((c) => ({ c, v: Number(cats[c] || 0) }))
      .filter((r) => r.v > 0)
      .sort((a, b) => b.v - a.v);
    if (rank.length > 0) {
      items.push({
        key: "top_category",
        text: t("profitability.improve.top_category",
          t(`profitability.category.${rank[0].c}`),
          moneyFmt(rank[0].v, lang),
          ((rank[0].v / total) * 100).toFixed(1),
        ),
      });
    }
    const missing = COST_CATEGORIES.filter((c) => !cats[c] || cats[c] === 0);
    if (missing.length > 0 && missing.length < COST_CATEGORIES.length) {
      items.push({
        key: "missing_categories",
        text: t("profitability.improve.missing_categories",
          missing.map((c) => t(`profitability.category.${c}`)).join(", "),
        ),
      });
    }
  }
  if (exp.supported && (snap.profit || {}).supported
      && exp.expected_margin_pct != null
      && (snap.profit || {}).actual_margin_pct != null) {
    const diff = snap.profit.actual_margin_pct - exp.expected_margin_pct;
    if (Math.abs(diff) >= 1) {
      items.push({
        key: "margin_variance",
        text: diff < 0
          ? t("profitability.improve.margin_shortfall", Math.abs(diff).toFixed(2))
          : t("profitability.improve.margin_surplus", diff.toFixed(2)),
      });
    }
  }
  if ((act.unconfirmed_count || 0) > 0) {
    items.push({ key: "unconfirmed",
      text: t("profitability.improve.confirm_before_review") });
  }
  if (items.length === 0) {
    items.push({ key: "not_yet",
      text: t("profitability.improve.not_yet") });
  }
  return (
    <section data-testid="profitability-section-improve" className="jc-card p-5 mb-6">
      <div className="overline">{t("profitability.section.improve_overline")}</div>
      <h2 className="text-lg font-semibold mb-2">
        {t("profitability.section.improve_title")}
      </h2>
      <p className="text-xs text-[var(--jc-text-soft)] mb-3">
        {t("profitability.section.improve_subtitle")}
      </p>
      <ul className="space-y-2" data-testid="improve-observations">
        {items.map((o) => (
          <li
            key={o.key}
            data-testid={`improve-obs-${o.key}`}
            className="text-sm rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)] px-3 py-2"
          >
            {o.text}
          </li>
        ))}
      </ul>
      <div className="mt-3 text-[11px] text-[var(--jc-text-soft)] italic">
        {t("profitability.improve.contractor_control_note")}
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------
// Main page.
// ---------------------------------------------------------------------

const Profitability = () => {
  const { t, lang } = useLang();
  const { pid } = useParams();
  // Phase 5A · Publish current-page workspace hint (backend
  // re-verifies the ID against the authenticated tenant).
  useLeviWorkspaceHint("profitability", {
    active_project_id: pid,
    current_section: "profitability",
  });
  const [snap, setSnap] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [entries, setEntries] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [savingMargin, setSavingMargin] = useState(false);
  const [marginDraft, setMarginDraft] = useState({ pct: "", source: "", note: "" });
  const [addingEntry, setAddingEntry] = useState(false);
  const [entryDraft, setEntryDraft] = useState(emptyEntryDraft);
  const [editingEntryId, setEditingEntryId] = useState("");
  const [editingCorrection, setEditingCorrection] = useState(false);
  const [correctionReason, setCorrectionReason] = useState("");
  const [addingDoc, setAddingDoc] = useState(false);
  const [docDraft, setDocDraft] = useState(emptyDocDraft);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, eRes, setRes, ctxRes] = await Promise.all([
        api.get(`/projects/${pid}/profitability/summary`),
        api.get(`/projects/${pid}/profitability/cost-entries`),
        api.get(`/projects/${pid}/profitability/settings`),
        api.get(`/projects/${pid}/profitability/levi-context`),
      ]);
      setSnap(sRes.data || null);
      setEntries(eRes.data || []);
      setSettings(setRes.data || {});
      setCtx(ctxRes.data || null);
      if (setRes.data?.expected_margin_pct != null) {
        setMarginDraft({
          pct: String(setRes.data.expected_margin_pct),
          source: setRes.data.expected_margin_source || "",
          note: setRes.data.note || "",
        });
      }
    } catch (_) {
      toast.error(t("profitability.toast.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [pid, t]);

  useEffect(() => { load(); }, [load]);

  const startTracking = async () => {
    setStarting(true);
    try {
      await api.post(`/projects/${pid}/profitability/start-tracking`);
      toast.success(t("profitability.toast.tracking_started"));
      await load();
    } catch (_) {
      toast.error(t("profitability.toast.tracking_start_failed"));
    } finally { setStarting(false); }
  };

  const saveMargin = async () => {
    const pct = parseFloat(marginDraft.pct);
    if (!Number.isFinite(pct) || pct < 0 || pct >= 100) {
      toast.error(t("profitability.toast.invalid_margin"));
      return;
    }
    setSavingMargin(true);
    try {
      await api.put(`/projects/${pid}/profitability/expected-margin`, {
        expected_margin_pct: pct,
        expected_margin_source: marginDraft.source || "",
        note: marginDraft.note || "",
      });
      toast.success(t("profitability.toast.margin_saved"));
      await load();
    } catch (_) {
      toast.error(t("profitability.toast.margin_save_failed"));
    } finally { setSavingMargin(false); }
  };

  const addEntry = async () => {
    const amount = parseFloat(entryDraft.amount);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error(t("profitability.toast.invalid_amount"));
      return;
    }
    try {
      await api.post(`/projects/${pid}/profitability/cost-entries`, {
        amount, category: entryDraft.category,
        classification: entryDraft.classification,
        vendor: entryDraft.vendor, incurred_date: entryDraft.incurred_date,
        description: entryDraft.description, source: "manual",
      });
      toast.success(t("profitability.toast.entry_added"));
      setEntryDraft(emptyEntryDraft);
      setAddingEntry(false);
      await load();
    } catch (e) {
      toast.error(t("profitability.toast.entry_add_failed"));
    }
  };

  const saveEntryEdit = async () => {
    const eid = editingEntryId;
    const amount = parseFloat(entryDraft.amount);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error(t("profitability.toast.invalid_amount"));
      return;
    }
    try {
      const body = {
        amount, category: entryDraft.category,
        classification: entryDraft.classification,
        vendor: entryDraft.vendor, incurred_date: entryDraft.incurred_date,
        description: entryDraft.description,
      };
      if (editingCorrection) {
        body.confirmation_correction = true;
        body.correction_reason = correctionReason || "";
      }
      await api.put(`/projects/${pid}/profitability/cost-entries/${eid}`, body);
      toast.success(t("profitability.toast.entry_saved"));
      setEditingEntryId("");
      setEditingCorrection(false);
      setCorrectionReason("");
      setEntryDraft(emptyEntryDraft);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail?.error === "confirmed_entry_requires_correction_flag"
        ? t("profitability.toast.needs_correction_flag")
        : t("profitability.toast.entry_save_failed");
      toast.error(msg);
    }
  };

  const confirmEntry = async (entry) => {
    try {
      await api.post(`/projects/${pid}/profitability/cost-entries/${entry.id}/confirm`);
      toast.success(t("profitability.toast.entry_confirmed"));
      await load();
    } catch (_) {
      toast.error(t("profitability.toast.entry_confirm_failed"));
    }
  };

  const excludeEntry = async (entry) => {
    try {
      await api.delete(`/projects/${pid}/profitability/cost-entries/${entry.id}`);
      toast.success(t("profitability.toast.entry_excluded"));
      await load();
    } catch (_) {
      toast.error(t("profitability.toast.entry_exclude_failed"));
    }
  };

  const restoreEntry = async (entry) => {
    try {
      await api.post(`/projects/${pid}/profitability/cost-entries/${entry.id}/restore`);
      toast.success(t("profitability.toast.entry_restored"));
      await load();
    } catch (_) {
      toast.error(t("profitability.toast.entry_restore_failed"));
    }
  };

  const startEntryEdit = (entry, correction) => {
    setEditingEntryId(entry.id);
    setEditingCorrection(correction);
    setCorrectionReason("");
    setEntryDraft({
      amount: String(entry.amount || ""),
      category: entry.category || "materials",
      classification: entry.classification || "actual",
      vendor: entry.vendor || "",
      incurred_date: entry.incurred_date || "",
      description: entry.description || "",
    });
    setAddingEntry(false);
    setAddingDoc(false);
  };

  const addDocSuggestion = async () => {
    const amount = parseFloat(docDraft.amount);
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error(t("profitability.toast.invalid_amount"));
      return;
    }
    if (!docDraft.source_document_id.trim()) {
      toast.error(t("profitability.toast.missing_document"));
      return;
    }
    try {
      await api.post(`/projects/${pid}/profitability/cost-entries/from-document`, {
        source_document_id: docDraft.source_document_id.trim(),
        amount, category: docDraft.category,
        classification: docDraft.classification,
        vendor: docDraft.vendor, incurred_date: docDraft.incurred_date,
        description: docDraft.description,
      });
      toast.success(t("profitability.toast.suggestion_created"));
      setDocDraft(emptyDocDraft);
      setAddingDoc(false);
      await load();
    } catch (e) {
      const err = e?.response?.data?.detail?.error;
      if (err === "invalid_source_document") {
        toast.error(t("profitability.toast.invalid_document"));
      } else {
        toast.error(t("profitability.toast.suggestion_failed"));
      }
    }
  };

  const finalConfirm = async () => {
    setConfirming(true);
    try {
      await api.post(`/projects/${pid}/profitability/confirm`);
      toast.success(t("profitability.toast.profitability_confirmed"));
      await load();
    } catch (e) {
      const err = e?.response?.data?.detail?.error;
      if (err === "tracking_not_started") {
        toast.error(t("profitability.toast.tracking_not_started"));
      } else if (err === "no_supported_actual_profit") {
        toast.error(t("profitability.toast.no_supported_actual_profit"));
      } else {
        toast.error(t("profitability.toast.confirm_failed"));
      }
    } finally { setConfirming(false); }
  };

  const hasSnap = !!snap;
  const trackingStarted = !!snap?.tracking_started;
  const confirmedAt = snap?.confirmed_at || "";
  const state = snap?.state || "not_started";

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to={`/app/projects/${pid}`}
            data-testid="profitability-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
          >
            <ArrowLeft size={14} /> {t("profitability.back")}
          </Link>
          <div className="flex items-center gap-2">
            <LineChart size={18} className="text-[var(--jc-navy)]" />
            <div>
              <div className="overline">{t("profitability.page_overline")}</div>
              <h1 className="text-lg font-semibold">{t("profitability.page_title")}</h1>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-sm text-[var(--jc-text-soft)] flex items-center gap-2">
            <Loader2 className="animate-spin" size={14} /> {t("profitability.loading")}
          </div>
        )}

        {!loading && hasSnap && (
          <>
            {/* First-use panel */}
            {!trackingStarted && state === "not_started" && (
              <section
                data-testid="profitability-first-use"
                className="jc-card p-5 mb-6 border-dashed"
              >
                <div className="overline">{t("profitability.first_use_overline")}</div>
                <h2 className="text-lg font-semibold mb-1">
                  {t("profitability.first_use_title")}
                </h2>
                <p className="text-sm text-[var(--jc-text-soft)] mb-3">
                  {t("profitability.first_use_body")}
                </p>
                <button
                  data-testid="start-tracking-btn"
                  onClick={startTracking}
                  disabled={starting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
                >
                  {starting ? <><Loader2 className="animate-spin" size={14} /> {t("profitability.starting")}</>
                    : <><ShieldCheck size={14} /> {t("profitability.start_tracking")}</>}
                </button>
              </section>
            )}

            <OverviewSection snap={snap} t={t} lang={lang} />

            {/* Expected margin form */}
            <section
              data-testid="profitability-section-expected-margin"
              className="jc-card p-5 mb-6"
            >
              <div className="overline">{t("profitability.section.margin_overline")}</div>
              <h2 className="text-lg font-semibold mb-2">
                {t("profitability.section.margin_title")}
              </h2>
              <p className="text-xs text-[var(--jc-text-soft)] mb-3">
                {t("profitability.section.margin_subtitle")}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[var(--jc-text-soft)]">
                    {t("profitability.margin.pct_label")}
                  </label>
                  <input
                    data-testid="expected-margin-input"
                    type="number" min="0" max="99.99" step="0.01"
                    value={marginDraft.pct}
                    onChange={(e) => setMarginDraft({ ...marginDraft, pct: e.target.value })}
                    className="mt-1 w-full jc-input"
                    placeholder="20"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-[var(--jc-text-soft)]">
                    {t("profitability.margin.source_label")}
                  </label>
                  <input
                    data-testid="expected-margin-source-input"
                    type="text" value={marginDraft.source}
                    onChange={(e) => setMarginDraft({ ...marginDraft, source: e.target.value })}
                    className="mt-1 w-full jc-input"
                    placeholder={t("profitability.margin.source_placeholder")}
                  />
                </div>
                <div className="md:col-span-1 flex items-end">
                  <button
                    data-testid="save-expected-margin-btn"
                    onClick={saveMargin}
                    disabled={savingMargin}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
                  >
                    {savingMargin ? <><Loader2 className="animate-spin" size={14} /> {t("profitability.saving")}</>
                      : t("profitability.margin.save")}
                  </button>
                </div>
              </div>
              {snap.expected?.supported && (
                <div className="mt-3 text-xs text-[var(--jc-text-soft)]">
                  <span className="font-semibold text-[var(--jc-text)]">
                    {t("profitability.margin.derived_note_prefix")}:
                  </span>{" "}
                  {t("profitability.margin.derived_note",
                    moneyFmt(snap.expected.expected_profit_amount, lang),
                    moneyFmt(snap.expected.implied_expected_cost, lang),
                  )}
                </div>
              )}
            </section>

            <RevenueSection snap={snap} t={t} lang={lang} />
            <CostsSection snap={snap} t={t} lang={lang} />

            {/* Cost entries + document suggestions */}
            <section
              data-testid="profitability-section-cost-entries"
              className="jc-card p-5 mb-6"
            >
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <div className="overline">{t("profitability.section.entries_overline")}</div>
                  <h2 className="text-lg font-semibold">
                    {t("profitability.section.entries_title")}
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="add-cost-entry-btn"
                    onClick={() => {
                      setAddingEntry(true);
                      setAddingDoc(false);
                      setEditingEntryId("");
                      setEntryDraft(emptyEntryDraft);
                    }}
                    className="text-xs font-semibold px-2.5 py-1.5 rounded-md jc-btn-primary inline-flex items-center gap-1"
                  >
                    <Plus size={12} /> {t("profitability.entry.add_manual")}
                  </button>
                  <button
                    data-testid="add-document-suggestion-btn"
                    onClick={() => {
                      setAddingDoc(true);
                      setAddingEntry(false);
                      setEditingEntryId("");
                      setDocDraft(emptyDocDraft);
                    }}
                    className="text-xs font-semibold px-2.5 py-1.5 rounded-md border border-[var(--jc-border)] hover:bg-[var(--jc-bg)] inline-flex items-center gap-1"
                  >
                    <FileText size={12} /> {t("profitability.entry.add_document")}
                  </button>
                </div>
              </div>
              <p className="text-xs text-[var(--jc-text-soft)] mb-3">
                {t("profitability.section.entries_subtitle")}
              </p>

              {(addingEntry || editingEntryId) && (
                <EntryForm
                  t={t}
                  draft={entryDraft}
                  setDraft={setEntryDraft}
                  onCancel={() => {
                    setAddingEntry(false);
                    setEditingEntryId("");
                    setEditingCorrection(false);
                    setCorrectionReason("");
                    setEntryDraft(emptyEntryDraft);
                  }}
                  onSave={editingEntryId ? saveEntryEdit : addEntry}
                  correction={editingCorrection}
                  correctionReason={correctionReason}
                  setCorrectionReason={setCorrectionReason}
                  editing={!!editingEntryId}
                />
              )}

              {addingDoc && (
                <DocumentSuggestionForm
                  t={t} draft={docDraft} setDraft={setDocDraft}
                  onCancel={() => { setAddingDoc(false); setDocDraft(emptyDocDraft); }}
                  onSave={addDocSuggestion}
                />
              )}

              {entries.length === 0 && !addingEntry && !addingDoc && (
                <div
                  data-testid="cost-entries-empty"
                  className="text-sm text-[var(--jc-text-soft)] italic border border-dashed border-[var(--jc-border)] rounded-md p-4 text-center"
                >
                  {t("profitability.entry.empty")}
                </div>
              )}

              <div className="space-y-2" data-testid="cost-entries-list">
                {entries.map((e) => (
                  <EntryRow
                    key={e.id}
                    entry={e}
                    onConfirm={confirmEntry}
                    onExclude={excludeEntry}
                    onRestore={restoreEntry}
                    onEdit={startEntryEdit}
                    t={t} lang={lang}
                  />
                ))}
              </div>
            </section>

            <LeviReviewSection ctx={ctx} t={t} lang={lang} />
            <ImproveNextJobSection snap={snap} t={t} lang={lang} />

            {/* Final confirmation */}
            <section
              data-testid="profitability-section-confirm"
              className="jc-card p-5 mb-6"
            >
              <div className="overline">{t("profitability.section.confirm_overline")}</div>
              <h2 className="text-lg font-semibold mb-1">
                {t("profitability.section.confirm_title")}
              </h2>
              <p className="text-xs text-[var(--jc-text-soft)] mb-3">
                {t("profitability.section.confirm_subtitle")}
              </p>
              {confirmedAt ? (
                <div
                  data-testid="profitability-confirmed-panel"
                  className="border border-emerald-200 bg-emerald-50 text-emerald-900 rounded-md p-3 text-sm flex items-start gap-2"
                >
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">{t("profitability.confirm.confirmed_state")}</div>
                    <div className="text-xs" data-testid="profitability-confirmed-at">
                      {t("profitability.confirm.confirmed_at")}: {confirmedAt}
                    </div>
                    <div className="text-xs mt-1">
                      {t("profitability.confirm.snapshot_note")}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {(snap.missing_data || []).length > 0 && (
                    <div
                      data-testid="confirm-warnings"
                      className="border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-3 text-xs"
                    >
                      <div className="font-semibold mb-1 flex items-center gap-1.5">
                        <AlertTriangle size={14} /> {t("profitability.confirm.warnings_heading")}
                      </div>
                      <ul className="list-disc ml-5 space-y-0.5">
                        {(snap.missing_data || []).map((f) => (
                          <li key={f}>{t(`profitability.missing_flag.${f}`)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <button
                    data-testid="final-confirm-btn"
                    onClick={finalConfirm}
                    disabled={confirming || !trackingStarted || !snap?.profit?.supported}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
                  >
                    {confirming ? <><Loader2 className="animate-spin" size={14} /> {t("profitability.confirm.confirming")}</>
                      : <><ShieldCheck size={14} /> {t("profitability.confirm.action")}</>}
                  </button>
                  {!trackingStarted && (
                    <div className="text-xs text-[var(--jc-text-soft)]">
                      {t("profitability.confirm.needs_tracking")}
                    </div>
                  )}
                  {/* D-11 · UI-side honesty guard when actual profit is unsupported. */}
                  {trackingStarted && !snap?.profit?.supported && (
                    <div
                      data-testid="confirm-unsupported-guard"
                      className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2"
                    >
                      {t("profitability.confirm.needs_supported_actual_profit")}
                    </div>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

// ---------------------------------------------------------------------
// Small forms — kept local to avoid file sprawl.
// ---------------------------------------------------------------------

const EntryForm = ({
  t, draft, setDraft, onCancel, onSave, editing, correction,
  correctionReason, setCorrectionReason,
}) => (
  <div
    data-testid="cost-entry-form"
    className="border border-[var(--jc-border)] rounded-md p-3 mb-3 bg-[var(--jc-bg)]"
  >
    <div className="text-xs font-semibold uppercase tracking-wider text-[var(--jc-text-soft)] mb-2">
      {correction
        ? t("profitability.entry.form_title_correction")
        : editing
          ? t("profitability.entry.form_title_edit")
          : t("profitability.entry.form_title_add")}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_amount")}
        </label>
        <input
          data-testid="entry-amount-input"
          type="number" min="0" step="0.01" value={draft.amount}
          onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
          className="mt-1 w-full jc-input"
        />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_category")}
        </label>
        <select
          data-testid="entry-category-input"
          value={draft.category}
          onChange={(e) => setDraft({ ...draft, category: e.target.value })}
          className="mt-1 w-full jc-input"
        >
          {COST_CATEGORIES.map((c) => (
            <option key={c} value={c}>{t(`profitability.category.${c}`)}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_classification")}
        </label>
        <select
          data-testid="entry-classification-input"
          value={draft.classification}
          onChange={(e) => setDraft({ ...draft, classification: e.target.value })}
          className="mt-1 w-full jc-input"
        >
          {COST_CLASSIFICATIONS.map((c) => (
            <option key={c} value={c}>{t(`profitability.classification.${c}`)}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_vendor")}
        </label>
        <input
          data-testid="entry-vendor-input"
          type="text" value={draft.vendor}
          onChange={(e) => setDraft({ ...draft, vendor: e.target.value })}
          className="mt-1 w-full jc-input"
        />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_incurred_date")}
        </label>
        <input
          data-testid="entry-date-input"
          type="date" value={draft.incurred_date}
          onChange={(e) => setDraft({ ...draft, incurred_date: e.target.value })}
          className="mt-1 w-full jc-input"
        />
      </div>
      <div className="md:col-span-3">
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_description")}
        </label>
        <input
          data-testid="entry-description-input"
          type="text" value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          className="mt-1 w-full jc-input"
        />
      </div>
      {correction && (
        <div className="md:col-span-3">
          <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
            {t("profitability.entry.field_correction_reason")}
          </label>
          <input
            data-testid="entry-correction-reason-input"
            type="text" value={correctionReason}
            onChange={(e) => setCorrectionReason(e.target.value)}
            className="mt-1 w-full jc-input"
            placeholder={t("profitability.entry.correction_placeholder")}
          />
        </div>
      )}
    </div>
    <div className="flex gap-2 mt-3 flex-wrap">
      <button
        data-testid="save-entry-btn"
        onClick={onSave}
        className="text-xs font-semibold px-3 py-1.5 rounded-md jc-btn-primary"
      >
        {editing ? t("profitability.entry.save_edit") : t("profitability.entry.save_add")}
      </button>
      <button
        data-testid="cancel-entry-btn"
        onClick={onCancel}
        className="text-xs font-semibold px-3 py-1.5 rounded-md border border-[var(--jc-border)] hover:bg-white"
      >
        {t("profitability.cancel")}
      </button>
    </div>
  </div>
);

const DocumentSuggestionForm = ({ t, draft, setDraft, onCancel, onSave }) => (
  <div
    data-testid="document-suggestion-form"
    className="border border-indigo-200 bg-indigo-50/40 rounded-md p-3 mb-3"
  >
    <div className="text-xs font-semibold uppercase tracking-wider text-indigo-900 mb-1">
      {t("profitability.doc.form_title")}
    </div>
    <p className="text-xs text-[var(--jc-text-soft)] mb-2" data-testid="doc-foundation-note">
      {t("profitability.doc.foundation_note")}
    </p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      <div className="md:col-span-3">
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.doc.field_document_id")}
        </label>
        <input
          data-testid="doc-source-id-input"
          type="text" value={draft.source_document_id}
          onChange={(e) => setDraft({ ...draft, source_document_id: e.target.value })}
          className="mt-1 w-full jc-input"
          placeholder={t("profitability.doc.field_document_id_placeholder")}
        />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_amount")}
        </label>
        <input
          data-testid="doc-amount-input"
          type="number" min="0" step="0.01" value={draft.amount}
          onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
          className="mt-1 w-full jc-input"
        />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_category")}
        </label>
        <select
          data-testid="doc-category-input"
          value={draft.category}
          onChange={(e) => setDraft({ ...draft, category: e.target.value })}
          className="mt-1 w-full jc-input"
        >
          {COST_CATEGORIES.map((c) => (
            <option key={c} value={c}>{t(`profitability.category.${c}`)}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_classification")}
        </label>
        <select
          data-testid="doc-classification-input"
          value={draft.classification}
          onChange={(e) => setDraft({ ...draft, classification: e.target.value })}
          className="mt-1 w-full jc-input"
        >
          {COST_CLASSIFICATIONS.map((c) => (
            <option key={c} value={c}>{t(`profitability.classification.${c}`)}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_vendor")}
        </label>
        <input
          data-testid="doc-vendor-input"
          type="text" value={draft.vendor}
          onChange={(e) => setDraft({ ...draft, vendor: e.target.value })}
          className="mt-1 w-full jc-input"
        />
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_incurred_date")}
        </label>
        <input
          data-testid="doc-date-input"
          type="date" value={draft.incurred_date}
          onChange={(e) => setDraft({ ...draft, incurred_date: e.target.value })}
          className="mt-1 w-full jc-input"
        />
      </div>
      <div className="md:col-span-3">
        <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--jc-text-soft)]">
          {t("profitability.entry.field_description")}
        </label>
        <input
          data-testid="doc-description-input"
          type="text" value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          className="mt-1 w-full jc-input"
        />
      </div>
    </div>
    <div className="flex gap-2 mt-3 flex-wrap">
      <button
        data-testid="save-document-suggestion-btn"
        onClick={onSave}
        className="text-xs font-semibold px-3 py-1.5 rounded-md jc-btn-primary"
      >
        {t("profitability.doc.save")}
      </button>
      <button
        data-testid="cancel-document-suggestion-btn"
        onClick={onCancel}
        className="text-xs font-semibold px-3 py-1.5 rounded-md border border-[var(--jc-border)] hover:bg-white"
      >
        {t("profitability.cancel")}
      </button>
    </div>
  </div>
);

export default Profitability;
